import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Search,
  MessageSquare,
  Car,
  KeyRound,
  MapPin,
  RotateCcw,
  ShieldCheck,
  Clock,
  ArrowRight,
} from "lucide-react";

export const Route = createFileRoute("/how-it-works")({
  head: () => ({
    meta: [
      { title: "How Luxury Car Rental Works in Miami | Creed Car Rental" },
      {
        name: "description",
        content:
          "How to rent a luxury car in Miami with Creed: browse the fleet, request your dates, get the car delivered to MIA, your hotel or Airbnb. Insurance included.",
      },
      { property: "og:title", content: "How Luxury Car Rental Works in Miami | Creed Car Rental" },
      {
        property: "og:description",
        content:
          "Browse, book, and drive — we deliver hand-picked luxury cars across Miami-Dade. See how easy renting with Creed can be.",
      },
      { property: "og:url", content: "https://creedcarental.com/how-it-works" },
    ],
    links: [{ rel: "canonical", href: "https://creedcarental.com/how-it-works" }],
  }),
  component: HowItWorks,
});

const steps = [
  {
    num: "01",
    Icon: Search,
    title: "Browse the fleet",
    body: "Pick your car, dates, and delivery location — MIA, FLL, South Beach hotels, Brickell condos, or your Airbnb. Everything you need is on the listing.",
  },
  {
    num: "02",
    Icon: MessageSquare,
    title: "Request a quote",
    body: "Submit your booking request with a few details. We verify availability, confirm delivery logistics, and send you an all-in quote within the hour.",
  },
  {
    num: "03",
    Icon: ShieldCheck,
    title: "Confirm your booking",
    body: "We confirm availability and arrange pickup details. A refundable security deposit holds your dates. Your rate includes insurance and daily mileage — no surprise fees.",
  },
  {
    num: "04",
    Icon: Car,
    title: "We bring it to you",
    body: "Your car arrives detailed, fueled, and ready. We do a quick walk-around, snap photos together, and hand over the keys. Most deliveries take 15 minutes.",
  },
  {
    num: "05",
    Icon: MapPin,
    title: "Drive",
    body: "Enjoy Miami your way. Full-coverage insurance and 24/7 roadside assistance are included. Text us anytime — you have the owner's number, not a call center.",
  },
  {
    num: "06",
    Icon: RotateCcw,
    title: "Return",
    body: "We pick up the car at your hotel, residence, or the airport. Same quick inspection, deposit released, and you're on your way. That's it.",
  },
];

const trust = [
  {
    Icon: ShieldCheck,
    title: "Insurance included",
    body: "State-required liability plus comprehensive coverage on every rental. No upsells at the counter.",
  },
  {
    Icon: Clock,
    title: "Replies within the hour",
    body: "Real person, real number. Call or text 7 days a week and get an answer fast — not a ticket queue.",
  },
  {
    Icon: KeyRound,
    title: "No hidden fees",
    body: "Your quote is all-in. Cleaning, refueling, and late fees only apply if policies are broken — and you know them upfront.",
  },
];

function HowItWorks() {
  return (
    <div className="min-h-screen bg-cream">
      <SiteHeader />

      {/* Hero header */}
      <section className="relative isolate overflow-hidden border-b border-border/60 bg-espresso py-20 md:py-24">
        <div
          className="pointer-events-none absolute inset-0 opacity-60"
          style={{
            background:
              "radial-gradient(60% 80% at 85% 20%, oklch(0.72 0.15 65 / 0.35), transparent 60%), radial-gradient(50% 70% at 10% 90%, oklch(0.72 0.15 65 / 0.18), transparent 60%)",
          }}
          aria-hidden
        />
        <svg
          className="pointer-events-none absolute -right-24 -top-24 h-[520px] w-[520px] text-accent/20"
          viewBox="0 0 600 600"
          fill="none"
          aria-hidden
        >
          <circle cx="300" cy="300" r="220" stroke="currentColor" strokeWidth="1" />
          <circle cx="300" cy="300" r="160" stroke="currentColor" strokeWidth="1" />
          <circle cx="300" cy="300" r="100" stroke="currentColor" strokeWidth="1" />
        </svg>
        <div className="relative mx-auto max-w-4xl px-6 text-center">
          <div className="text-xs uppercase tracking-[0.3em] text-accent">
            Simple & transparent
          </div>
          <h1 className="mt-4 font-display text-5xl text-cream md:text-6xl">
            How renting works
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-cream/70">
            No apps to download, no call centers to sit in. Browse, book, and drive — we handle the rest.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button
              asChild
              size="lg"
              className="bg-accent text-espresso hover:bg-accent/90"
            >
              <Link to="/fleet">
                Browse the fleet <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-cream/30 text-cream hover:bg-cream/10"
            >
              <Link to="/contact">Ask a question</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="mx-auto max-w-6xl px-6 py-20">
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {steps.map((s) => (
            <Card
              key={s.num}
              className="rounded-tr-3xl border-border/60 bg-card p-6 shadow-sm"
            >
              <div className="flex items-center justify-between">
                <s.Icon className="h-6 w-6 text-accent" />
                <span aria-hidden className="font-display text-3xl text-espresso/40">
                  {s.num}
                </span>
              </div>
              <div className="mt-4 font-display text-xl text-espresso">
                {s.title}
              </div>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {s.body}
              </p>
            </Card>
          ))}
        </div>
      </section>

      {/* Trust strip */}
      <section className="border-y border-border/60 bg-secondary/40">
        <div className="mx-auto max-w-6xl px-6 py-16">
          <div className="text-center">
            <div className="text-sm uppercase tracking-[0.25em] text-accent">
              The fine print, upfront
            </div>
            <h2 className="mt-2 font-display text-3xl text-espresso md:text-4xl">
              What you can count on
            </h2>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {trust.map((t) => (
              <div
                key={t.title}
                className="rounded-tr-3xl bg-card p-6 shadow-sm"
              >
                <t.Icon className="h-7 w-7 text-accent" />
                <div className="mt-4 font-display text-xl text-espresso">
                  {t.title}
                </div>
                <p className="mt-2 text-sm text-muted-foreground">{t.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick timeline visual */}
      <section className="mx-auto max-w-4xl px-6 py-20">
        <div className="text-center">
          <div className="text-sm uppercase tracking-[0.25em] text-accent">
            Typical timeline
          </div>
          <h2 className="mt-2 font-display text-3xl text-espresso">
            From quote to keys
          </h2>
        </div>
        <div className="mt-10 space-y-6">
          {[
            { label: "Quote request sent", time: "Minute 0", desc: "You submit your dates, car, and delivery address." },
            { label: "Availability confirmed", time: "Within 1 hour", desc: "We check the calendar and confirm delivery logistics." },
            { label: "Booking locked in", time: "Same day", desc: "Deposit placed, rental agreement signed, insurance verified." },
            { label: "Car delivered", time: "Pickup day", desc: "Walk-around, photos, keys — you're on the road in 15 minutes." },
          ].map((item, i) => (
            <div
              key={i}
              className="flex flex-col gap-4 rounded-lg border border-border bg-card p-6 sm:flex-row sm:items-center sm:gap-6"
            >
              <div className="shrink-0 sm:w-40">
                <div className="text-xs font-medium uppercase tracking-wider text-accent">
                  {item.time}
                </div>
              </div>
              <div className="flex-1">
                <div className="font-display text-lg text-espresso">
                  {item.label}
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  {item.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-4xl px-6 pb-20">
        <div className="rounded-lg bg-primary p-10 text-center text-primary-foreground">
          <h2 className="font-display text-3xl md:text-4xl">
            Ready when you are.
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-primary-foreground/80">
            Browse the fleet, send a quick request, and we'll have a quote back
            to you within the hour.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Button
              asChild
              size="lg"
              variant="secondary"
              className="bg-cream text-espresso hover:bg-cream/90"
            >
              <Link to="/fleet">
                Browse the fleet <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-cream/40 bg-transparent text-cream hover:bg-cream/10 hover:text-cream"
            >
              <Link to="/contact">Get in touch</Link>
            </Button>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
